import { useCallback, useEffect, useRef, useState } from 'react';
import { useKeycloak } from '../components/KeycloakProvider';
import { useApiClient } from './useApiClient';

interface LabelItem {
  id: string;
  labelName: string;
  name: string;
  createTime: string;
}

interface LabelsListResponse {
  items: LabelItem[];
  nextPageToken?: string | null;
  totalSize?: number | null;
}

// Global cache shared across all hook instances — fetch once, share everywhere
let globalLabels: string[] | null = null;
let globalFetchPromise: Promise<string[]> | null = null;

/**
 * Hook for fetching available labels from GET /labels endpoint.
 * Uses a global cache so labels are fetched only once across all component instances.
 */
export const useAvailableLabels = () => {
  const { keycloak, initialized } = useKeycloak();
  const apiClient = useApiClient();

  const [labels, setLabels] = useState<string[]>(globalLabels ?? []);
  const [loading, setLoading] = useState(globalLabels === null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const fetchLabels = useCallback(async (force = false) => {
    if (!initialized || !keycloak?.authenticated) {
      setLoading(false);
      return;
    }

    // Return cached result if available and not forced
    if (globalLabels !== null && !force) {
      setLabels(globalLabels);
      setLoading(false);
      return;
    }

    // If another instance is already fetching, wait for it
    if (globalFetchPromise && !force) {
      try {
        const result = await globalFetchPromise;
        if (mountedRef.current) {
          setLabels(result);
          setLoading(false);
        }
      } catch {
        if (mountedRef.current) setLoading(false);
      }
      return;
    }

    setLoading(true);

    globalFetchPromise = apiClient.get<LabelsListResponse>('/labels?pageSize=100')
      .then((response) => {
        const labelNames = response.items.map((item) => item.labelName).sort();
        globalLabels = labelNames;
        return labelNames;
      });

    try {
      const result = await globalFetchPromise;
      if (mountedRef.current) {
        setLabels(result);
      }
    } catch (err) {
      console.error('Failed to fetch labels:', err);
      globalLabels = null;
    } finally {
      globalFetchPromise = null;
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [apiClient, initialized, keycloak?.authenticated]);

  useEffect(() => {
    fetchLabels();
  }, [fetchLabels]);

  return { labels, loading, refresh: () => fetchLabels(true) };
};
